import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconExternalLink,
  IconRefresh,
  IconWallet,
} from "@tabler/icons-react";
import type { Address } from "viem";
import { useAuth } from "@/auth/useAuth";
import { AdminApiError } from "@/api/client";
import {
  getCollateralBalance,
  prepareCollateralWithdrawal,
  reportCollateralWithdrawal,
  type CollateralBalance,
} from "@/api/collateralWithdrawals";
import { COLLECTIONS } from "@/firestore/collections";
import { useFirestoreQuery } from "@/firestore/hooks";
import type { CollateralWithdrawalDoc } from "@/firestore/types";
import { connectWallet, sendWithdrawAsset } from "@/wallet/collateralWithdraw";
import { PageHeader } from "@/components/PageHeader";
import { Money } from "@/components/Money";
import { Mono } from "@/components/Mono";
import { StatusBadge } from "@/components/StatusBadge";
import { Timestamp } from "@/components/Timestamp";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/;

function errMessage(e: unknown): string {
  if (e instanceof AdminApiError) {
    return `${e.message}${e.status ? ` (HTTP ${e.status}${e.code ? `, ${e.code}` : ""})` : ""}`;
  }
  return e instanceof Error ? e.message : String(e);
}

/** Groups the integer part of a decimal USDC string with thousands separators (no float — precision-safe). */
function formatUsdc(value: string): string {
  const [int, frac] = value.split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return frac !== undefined ? `${grouped}.${frac}` : grouped;
}

/** Derives the display state: a still-`requested` row past its signature expiry reads as "expired". */
function displayState(row: CollateralWithdrawalDoc): string {
  if (row.state === "requested" && new Date(row.expiresAt).getTime() < Date.now()) {
    return "expired";
  }
  return row.state;
}

export function CollateralWithdrawalsPage() {
  const { getIdToken, capabilities } = useAuth();
  const [connected, setConnected] = useState<Address | null>(null);
  const [amount, setAmount] = useState("");
  const [recipient, setRecipient] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<string | null>(null);

  // Reads are gated on `superAdmin` by the Firestore rule — only subscribe when allowed.
  const { data, loading, error } = useFirestoreQuery<CollateralWithdrawalDoc>(
    COLLECTIONS.collateralWithdrawals,
    { orderBy: [["createdAt", "desc"]] },
    capabilities.superAdmin,
  );

  // The collateral balance is a live Rain read (not a Firestore projection), so it is fetched
  // imperatively via the super-admin backend endpoint and refreshed on demand / after a withdrawal.
  const [balance, setBalance] = useState<CollateralBalance | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState<string | null>(null);

  const loadBalance = useCallback(async () => {
    setBalanceLoading(true);
    setBalanceError(null);
    try {
      setBalance(await getCollateralBalance(await getIdToken()));
    } catch (e) {
      setBalanceError(errMessage(e));
    } finally {
      setBalanceLoading(false);
    }
  }, [getIdToken]);

  useEffect(() => {
    if (capabilities.superAdmin) void loadBalance();
  }, [capabilities.superAdmin, loadBalance]);

  const crumbs = [{ label: "Treasury" }, { label: "Collateral Withdrawals" }];

  if (!capabilities.superAdmin) {
    return (
      <>
        <PageHeader
          title="Collateral Withdrawals"
          subtitle="Withdraw USDC from the Rain tenant collateral to a controlled address."
          crumbs={crumbs}
        />
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Super-admin only">
          This treasury surface requires the <Code>superAdmin</Code> role. Reads and actions are
          both gated on it (the backend and Firestore rules re-enforce it). Ask an owner to grant
          the role on your operator account.
        </Alert>
      </>
    );
  }

  const amountValid = Number(amount) > 0 && Number.isFinite(Number(amount));
  const recipientValid = ADDRESS_RE.test(recipient.trim());
  const canSubmit = !!connected && amountValid && recipientValid && !submitting;

  async function onConnect() {
    setConnecting(true);
    try {
      setConnected(await connectWallet());
    } catch (e) {
      notifications.show({
        color: "red",
        title: "Wallet connection failed",
        message: errMessage(e),
      });
    } finally {
      setConnecting(false);
    }
  }

  async function onWithdraw() {
    if (!connected) return;
    setSubmitting(true);
    let broadcastHash: string | undefined;
    try {
      setStep("Preparing — recipient allowlist + cap, Rain executor signature…");
      const token = await getIdToken(true);
      const prepared = await prepareCollateralWithdrawal(token, {
        amount: amount.trim(),
        recipient: recipient.trim(),
        adminAddress: connected,
        idempotencyKey: crypto.randomUUID(),
      });

      setStep("Awaiting wallet signature + broadcast on Base…");
      const { txHash, status } = await sendWithdrawAsset(prepared.unsignedWithdrawAsset, connected);
      broadcastHash = txHash;

      setStep("Recording the on-chain outcome…");
      const reportToken = await getIdToken(true);
      await reportCollateralWithdrawal(reportToken, {
        id: prepared.withdrawal.id,
        updatedAt: prepared.withdrawal.updatedAt,
        txHash,
        status,
      });

      notifications.show({
        color: status === "confirmed" ? "teal" : "orange",
        title: `Withdrawal ${status}`,
        message: `Transaction ${txHash}. It will appear in the table momentarily.`,
        autoClose: 8000,
      });
      setAmount("");
      setRecipient("");
      void loadBalance();
    } catch (e) {
      const suffix = broadcastHash
        ? ` — NOTE: transaction ${broadcastHash} WAS broadcast; only recording it failed. Verify on Basescan; the record may stay 'requested'.`
        : "";
      notifications.show({
        color: "red",
        title: "Withdrawal failed",
        message: errMessage(e) + suffix,
        autoClose: 12000,
      });
    } finally {
      setSubmitting(false);
      setStep(null);
    }
  }

  return (
    <>
      <PageHeader
        title="Collateral Withdrawals"
        subtitle="Withdraw USDC from the Rain tenant collateral to a controlled address (Base). The backend prepares + records; your connected wallet signs + broadcasts."
        crumbs={crumbs}
      />

      <Card withBorder radius="md" padding="lg" mb="lg">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={6} style={{ flex: 1 }}>
            <Text size="sm" c="dimmed">
              Tenant collateral balance (Base)
            </Text>
            {balanceError ? (
              <Text c="red" size="sm">
                {balanceError}
              </Text>
            ) : balance ? (
              <>
                <Text fw={700} fz={28} lh={1.1}>
                  {formatUsdc(balance.balance)}{" "}
                  <Text span fz={18} c="dimmed" fw={500}>
                    USDC
                  </Text>
                </Text>
                <Group gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    Contract
                  </Text>
                  <Anchor
                    href={`https://basescan.org/address/${balance.proxy}`}
                    target="_blank"
                    rel="noreferrer"
                    size="sm"
                  >
                    <Group gap={4} wrap="nowrap">
                      <Mono value={balance.proxy} short copy={false} />
                      <IconExternalLink size={13} />
                    </Group>
                  </Anchor>
                </Group>
                <Group gap="xs" align="center">
                  <Text size="sm" c="dimmed">
                    Registered admins
                  </Text>
                  {balance.adminAddresses.length > 0 ? (
                    balance.adminAddresses.map((a) => <Mono key={a} value={a} short />)
                  ) : (
                    <Text span c="dimmed">
                      —
                    </Text>
                  )}
                </Group>
              </>
            ) : (
              <Text size="sm" c="dimmed">
                {balanceLoading ? "Loading…" : "—"}
              </Text>
            )}
          </Stack>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconRefresh size={16} />}
            loading={balanceLoading}
            onClick={() => void loadBalance()}
          >
            Refresh
          </Button>
        </Group>
      </Card>

      <Card withBorder radius="md" padding="lg" mb="lg">
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Text fw={600}>New withdrawal</Text>
            {connected ? (
              <Group gap="xs">
                <Badge color="teal" variant="light" radius="sm">
                  Wallet connected
                </Badge>
                <Mono value={connected} short />
              </Group>
            ) : (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconWallet size={16} />}
                loading={connecting}
                onClick={onConnect}
              >
                Connect admin wallet
              </Button>
            )}
          </Group>

          <Text size="sm" c="dimmed">
            The connected wallet must be the registered collateral-admin EOA — it is sent to Rain as
            the signer and is checked as <Code>msg.sender</Code> on-chain.
          </Text>

          <Group grow align="flex-start">
            <TextInput
              label="Amount (USDC)"
              placeholder="1.00"
              value={amount}
              onChange={(e) => setAmount(e.currentTarget.value)}
              error={amount.length > 0 && !amountValid ? "Enter a positive amount" : undefined}
              disabled={submitting}
            />
            <TextInput
              label="Recipient address"
              placeholder="0x…"
              value={recipient}
              onChange={(e) => setRecipient(e.currentTarget.value)}
              error={
                recipient.length > 0 && !recipientValid ? "Enter a valid 0x address" : undefined
              }
              disabled={submitting}
            />
          </Group>

          <Group justify="space-between" align="center">
            <Text size="sm" c="dimmed">
              {step ?? "Recipient must be on the server-side allowlist; amount is capped."}
            </Text>
            <Button color="red" loading={submitting} disabled={!canSubmit} onClick={onWithdraw}>
              Prepare &amp; withdraw
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="lg">
        <Text fw={600} mb="md">
          History
        </Text>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} />
        ) : data.length === 0 ? (
          <EmptyState
            title="No collateral withdrawals yet"
            description="Prepared withdrawals appear here in real time."
          />
        ) : (
          <Table.ScrollContainer minWidth={880}>
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>State</Table.Th>
                  <Table.Th>Amount</Table.Th>
                  <Table.Th>Recipient</Table.Th>
                  <Table.Th>Tx</Table.Th>
                  <Table.Th>Requested by</Table.Th>
                  <Table.Th>Created</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.map((row) => (
                  <Table.Tr key={row._docId}>
                    <Table.Td>
                      <Mono value={row.id} short />
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge state={displayState(row)} />
                    </Table.Td>
                    <Table.Td>
                      <Money micro={row.amountMicroUsdc} />
                    </Table.Td>
                    <Table.Td>
                      <Mono value={row.recipient} short />
                    </Table.Td>
                    <Table.Td>
                      {row.txHash ? (
                        <Anchor
                          href={`https://basescan.org/tx/${row.txHash}`}
                          target="_blank"
                          rel="noreferrer"
                          size="sm"
                        >
                          <Group gap={4} wrap="nowrap">
                            <Mono value={row.txHash} short copy={false} />
                            <IconExternalLink size={13} />
                          </Group>
                        </Anchor>
                      ) : (
                        <Text span c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Mono value={row.requestedBy} short />
                    </Table.Td>
                    <Table.Td>
                      <Timestamp value={row.createdAt} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>
    </>
  );
}
